(() => {
  function referralCode(customerId) {
    if (typeof customerId !== 'string' || !/^[1-9]\d{0,18}$/.test(customerId) || typeof BigInt !== 'function') return '';
    const id = BigInt(customerId);
    return id <= BigInt('9223372036854775807') ? 'PM' + id.toString(36).toUpperCase() : '';
  }

  function productShareUrl(productUrl, origin, variantId, customerId) {
    const url = new URL(productUrl, origin);
    if (url.origin !== new URL(origin).origin || !/^https?:$/.test(url.protocol)) throw new Error('Invalid product origin');
    url.search = '';
    url.hash = '';
    if (/^[1-9]\d*$/.test(String(variantId || ''))) url.searchParams.set('variant', variantId);
    const code = referralCode(customerId);
    if (code) url.searchParams.set('ref', code);
    return url.href;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { referralCode, productShareUrl };
  if (typeof customElements === 'undefined' || customElements.get('petmagic-product-share')) return;

  class PetmagicProductShare extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.customerId = this.dataset.customerId || '';
      this.stale = false;
      this.sessionVersion = 0;
      this.actionVersion = 0;
      this.status = this.querySelector('[data-status]');
      this.fallback = this.querySelector('[data-fallback]');
      this.link = this.querySelector('[data-link]');
      this.buttons = [...this.querySelectorAll('[data-share], [data-copy]')];
      this.querySelector('[data-share]').addEventListener('click', () => this.share(), options);
      this.querySelector('[data-copy]').addEventListener('click', () => this.copy(), options);
      this.querySelector('[data-close]').addEventListener('click', () => {
        this.fallback.hidden = true;
        this.querySelector('[data-copy]').focus();
      }, options);
      this.link.addEventListener('click', () => this.link.select(), options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.fallback.hidden) {
          this.fallback.hidden = true;
          this.querySelector('[data-copy]').focus();
        }
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.invalidateCustomer();
        else this.refreshCustomer();
      }, options);
      window.addEventListener('blur', () => this.invalidateCustomer(), options);
      window.addEventListener('focus', () => this.refreshCustomer(), options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) {
          this.invalidateCustomer();
          this.refreshCustomer();
        }
      }, options);
      this.hidden = false;
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.events = null;
    }

    selectedVariant() {
      const validIds = new Set((this.dataset.variantIds || '').split(',').concat(this.dataset.variantId));
      const currentUrl = new URL(location.href);
      const productUrl = new URL(this.dataset.productUrl, location.origin);
      const urlVariant = currentUrl.searchParams.get('variant');
      const productPath = path => path.replace(/\/collections\/[^/]+(?=\/products\/)/, '');
      if (productPath(currentUrl.pathname) === productPath(productUrl.pathname) && validIds.has(urlVariant)) return urlVariant;
      const form = document.getElementById(this.dataset.formId);
      const current = form?.querySelector('[name="id"]')?.value;
      return validIds.has(current) ? current : this.dataset.variantId;
    }

    url() {
      return productShareUrl(this.dataset.productUrl, location.origin, this.selectedVariant(), this.customerId);
    }

    clearFeedback() {
      this.status.textContent = '';
      this.fallback.hidden = true;
      this.link.value = '';
    }

    invalidateCustomer() {
      this.sessionVersion += 1;
      this.request?.abort();
      this.customerId = '';
      this.stale = true;
      this.clearFeedback();
      this.buttons.forEach(button => { button.disabled = true; });
    }

    async refreshCustomer() {
      if (!this.stale || this.request || document.hidden) return;
      const request = new AbortController();
      const version = this.sessionVersion;
      this.request = request;
      const timer = setTimeout(() => request.abort(), 8000);
      try {
        const url = new URL(productShareUrl(this.dataset.productUrl, location.origin, this.selectedVariant(), ''));
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: request.signal });
        if (!response.ok) throw new Error('Session refresh failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = [...html.querySelectorAll('petmagic-product-share')].find(element =>
          element.dataset.productId === this.dataset.productId && element.dataset.sectionId === this.dataset.sectionId);
        if (!fresh) throw new Error('Missing product section');
        if (version === this.sessionVersion && !document.hidden) this.customerId = fresh.dataset.customerId || '';
      } catch (_) {
        if (version === this.sessionVersion) this.customerId = '';
      } finally {
        clearTimeout(timer);
        this.request = null;
        if (version === this.sessionVersion) {
          this.stale = document.hidden;
          this.buttons.forEach(button => { button.disabled = this.stale; });
        } else if (this.isConnected && !document.hidden) {
          this.refreshCustomer();
        }
      }
    }

    async share() {
      if (this.stale) return;
      this.clearFeedback();
      const url = this.url();
      const version = this.sessionVersion;
      const action = ++this.actionVersion;
      if (!navigator.share) return this.copy(url);
      try {
        await navigator.share({ title: this.dataset.productTitle, url });
      } catch (error) {
        if (error.name !== 'AbortError' && this.currentAction(version, action)) this.showLink(url);
      }
    }

    async copy(url = this.url()) {
      if (this.stale) return;
      const version = this.sessionVersion;
      const action = ++this.actionVersion;
      this.clearFeedback();
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(url);
        if (this.currentAction(version, action)) this.status.textContent = this.dataset.copied;
      } catch (_) {
        if (this.currentAction(version, action)) {
          this.status.textContent = this.dataset.copyFailed;
          this.showLink(url);
        }
      }
    }

    currentAction(version, action) {
      return this.isConnected && !this.stale && version === this.sessionVersion && action === this.actionVersion;
    }

    showLink(url) {
      if (this.stale) return;
      this.fallback.hidden = false;
      this.link.value = url;
      this.link.focus();
      this.link.select();
    }
  }

  customElements.define('petmagic-product-share', PetmagicProductShare);
})();
